import React, { useEffect, useState } from 'react';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Button from '../../components/button/Button';
import Image from '../../components/image/Image';
import Table from '../../components/table/table';
import TableSkeleton from '../../components/TableSkeleton/TableSkeleton';
import RecalculateCommissionModal from './RecalculateCommissionModal';
import { getGuaranteeCommission, recalculateGuaranteeCommission } from '../../services/GuaranteesService';
import { formatCommissionCurrencyDisplay } from '../../utils/commissionConfigurationPayload';
import { formatCurrency as formatNumber } from '../../utils/Masks';
import { ITableData } from '../../types/interfaces';

interface CommissionsTabProps {
  guaranteeId: string;
  guaranteeType: 'loan' | 'check' | 'promissory_note' | 'commercial_endorsement';
  idGlobal: string;
  montoAval?: number;
  currency?: string;
}

interface CommissionData {
  global_id: number;
  rate_pct: number;
  commission_amount: number;
  expense_pct: number;
  expense_amount: number;
  referral_pct: number;
  referral_amount: number;
  referrer_name_id: number | null;
  referrer_name: string | null;
}

const formatAmount = (amount: number, currencyCode?: string): string => {
  const symbol = formatCommissionCurrencyDisplay(currencyCode || 'ARS');
  return `${symbol} ${formatNumber(amount)}`;
};

const CommissionsTab: React.FC<CommissionsTabProps> = ({ guaranteeId, guaranteeType, idGlobal, montoAval, currency }) => {
  const [commission, setCommission] = useState<CommissionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchCommission();
  }, [idGlobal]);

  const fetchCommission = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getGuaranteeCommission(idGlobal);

      if (response.success && 'data' in response && response.data) {
        setCommission(response.data);
      } else {
        setCommission(null);
      }
    } catch (err: any) {
      if (err?.response?.status === 204) {
        setCommission(null);
      } else {
        console.error('Error fetching guarantee commission:', err);
        setError('Error al cargar la comisión');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async (ratePct: number, expensePct: number, referralPct: number) => {
    const response = await recalculateGuaranteeCommission(idGlobal, ratePct, expensePct, referralPct);
    if (response.success && 'data' in response && response.data) {
      setCommission(response.data);
    } else {
      await fetchCommission();
    }
  };

  const columns = [
    { name: '', selector: (row: any) => row.label, width: '180px', minWidth: '180px', grow: 0 },
    { name: 'Monto Aval', selector: (row: any) => row.monto_aval },
    { name: 'Tasa %', selector: (row: any) => row.tasa_pct },
    { name: 'Monto', selector: (row: any) => row.monto_comision },
    { name: '% Gasto', selector: (row: any) => row.gasto_pct },
    { name: 'Monto', selector: (row: any) => row.monto_gasto },
    { name: 'Total de Comisión', selector: (row: any) => row.total },
    { name: '% al referido', selector: (row: any) => row.referral_pct },
    { name: 'Monto al referido', selector: (row: any) => row.monto_referido },
    { name: 'Referido', selector: (row: any) => row.referido },
  ];

  if (loading) {
    return (
      <Card margin={{ top: '4' }}>
        <TableSkeleton rows={2} columns={7} />
      </Card>
    );
  }

  if (error) {
    return (
      <Card
        display='grid'
        justifyContent='center'
        margin={{ top: '11', bottom: '11' }}
        width='100%'
      >
        <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
          <Image name='folder_empty' />
        </Card>
        <P variant='primary' align='center' withMargin={false} weight='bold'>
          {error}
        </P>
      </Card>
    );
  }

  if (!commission) {
    return (
      <>
        <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ top: '4' }}>
          <H
            type='5'
            variant='neutral'
            weight='bold'
            modifier='strong'
            withMargin={false}
          >
            Comisiones por líneas
          </H>
        </Card>
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='100%'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No hay comisiones registradas.
          </P>
        </Card>
      </>
    );
  }

  const total = commission.commission_amount + commission.expense_amount;
  const tableData: ITableData = { page: 0, size: 1, totalElements: 1, totalPages: 1 };

  const gridData = [
    {
      label: 'Comisión Operación',
      monto_aval: montoAval != null ? formatAmount(montoAval, currency) : '-',
      tasa_pct: `${commission.rate_pct} %`,
      monto_comision: formatAmount(commission.commission_amount, currency),
      gasto_pct: `${commission.expense_pct} %`,
      monto_gasto: formatAmount(commission.expense_amount, currency),
      total: formatAmount(total, currency),
      referral_pct: `${commission.referral_pct} %`,
      monto_referido: formatAmount(commission.referral_amount, currency),
      referido: commission.referrer_name ?? '—',
    },
  ];

  return (
    <>
      <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ top: '4' }}>
        <H
          type='5'
          variant='neutral'
          weight='bold'
          modifier='strong'
          withMargin={false}
        >
          Comisiones por líneas
        </H>
        <Button variant='tertiary' onClick={() => setIsModalOpen(true)}>
          Recalcular
        </Button>
      </Card>

      <Card margin={{ top: '4' }} justifyContent='center' alignItems='center'>
        <Table
          columns={columns}
          data={gridData}
          tableData={tableData}
          handleChangePage={() => {}}
        />
      </Card>

      <RecalculateCommissionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onRecalculate={handleRecalculate}
        currentRatePct={commission.rate_pct}
        currentExpensePct={commission.expense_pct}
        currentReferralPct={commission.referral_pct}
        hasReferrer={commission.referrer_name_id != null}
      />
    </>
  );
};

export default CommissionsTab;
